import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Observable, catchError, from, map, of, shareReplay, tap } from 'rxjs'
import { Injectable, inject } from '@angular/core'

import { ClientPreoffers, PreofferDetail, PreofferMode, PreofferSettingsOverview, PreofferStatus, PreofferSummary } from '@data/interfaces/preoffer.interface'
import { fileNameFromContentDisposition } from '@data/utils/content-disposition.util'
import { SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { environment } from '@env/environment'

/**
 * Preoferta (plan v4.3 §9.3, §9.4 y §2b). La preoferta es del cliente (D-72): la tarjeta de su Calculadora, el
 * renglón de su ficha y la píldora de cada arrendamiento suyo piden lo mismo. Cada lectura se comparte hasta que alguien
 * llama `refresh` (después de reenviar o de que Legal decida).
 */
@Injectable({ providedIn: 'root' })
export class ApiPreofferService {
  private readonly http = inject(HttpClient)
  private readonly cache = new Map<string, Observable<SnapshotResponse<ClientPreoffers>>>()
  private mode$: Observable<SnapshotResponse<{ mode: PreofferMode }>> | null = null

  byClient(clientId: string): Observable<SnapshotResponse<ClientPreoffers>> {
    return this.shared(`lease-preoffers/by-client/${clientId}`)
  }

  //* Desde un arrendamiento: la API resuelve a su cliente y devuelve la de él.
  byLease(leaseId: string): Observable<SnapshotResponse<ClientPreoffers>> {
    return this.shared(`lease-preoffers/by-lease/${leaseId}`)
  }

  /** La siguiente lectura va al servidor. Se limpia todo: la misma preoferta se lee por cliente y por arrendamiento. */
  refresh(): void {
    this.cache.clear()
  }

  reviews(status: PreofferStatus = 'legal_review'): Observable<SnapshotResponse<PreofferSummary[]>> {
    return this.wrap(this.http.get(`${environment.apiUrl}lease-preoffers/reviews`, { params: { status } }), 'No pudimos cargar la bandeja.')
  }

  detail(id: string): Observable<SnapshotResponse<PreofferDetail>> {
    return this.wrap(this.http.get(`${environment.apiUrl}lease-preoffers/${id}`), 'No pudimos cargar la preoferta.')
  }

  review(id: string, body: { decision: 'authorized' | 'returned'; reason?: string }): Observable<SnapshotResponse<PreofferSummary>> {
    return this.wrap(this.http.post(`${environment.apiUrl}lease-preoffers/${id}/review`, body), 'No se pudo guardar la decisión.')
  }

  resend(id: string): Observable<SnapshotResponse<{ queued: boolean }>> {
    return this.wrap(this.http.post(`${environment.apiUrl}lease-preoffers/${id}/resend`, {}), 'No se pudo reenviar la preoferta.')
  }

  document(id: string): Observable<SnapshotResponse<{ blob: Blob; fileName: string }>> {
    return this.http.get(`${environment.apiUrl}lease-preoffers/${id}/document`, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>): SnapshotResponse<{ blob: Blob; fileName: string }> => {
        if (!response.body) return { error: true, msg: 'El servidor no entregó el PDF', data: null, status: response.status }
        const fileName = fileNameFromContentDisposition(response.headers.get('content-disposition')) ?? 'preoferta.pdf'
        return { error: false, msg: '', data: { blob: response.body, fileName }, status: response.status }
      }),
      catchError((e: HttpErrorResponse) => {
        //* Con `responseType: 'blob'` el cuerpo del error también llega como Blob: se lee para no perder el mensaje.
        const body$ =
          e?.error instanceof Blob
            ? from(e.error.text()).pipe(
                map((text) => JSON.parse(text)),
                catchError(() => of(null)),
              )
            : of(e?.error)
        return body$.pipe(map((body) => this.toError<{ blob: Blob; fileName: string }>(e, 'No se pudo abrir el PDF', body)))
      }),
    )
  }

  /** §9.1 · El modo vigente, para la franja de la calculadora. Una petición por sesión; cambiar el modo la invalida. */
  mode(): Observable<SnapshotResponse<{ mode: PreofferMode }>> {
    if (!this.mode$) {
      this.mode$ = this.wrap<{ mode: PreofferMode }>(this.http.get(`${environment.apiUrl}lease-preoffers/mode`), 'No pudimos leer el modo de la preoferta.').pipe(
        tap((response) => {
          //* Un error no se guarda: la siguiente pestaña lo vuelve a intentar.
          if (response.error) this.mode$ = null
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      )
    }
    return this.mode$
  }

  settings(): Observable<SnapshotResponse<PreofferSettingsOverview>> {
    return this.wrap(this.http.get(`${environment.apiUrl}lease-preoffer-settings`), 'No pudimos cargar la configuración de la preoferta.')
  }

  updateThresholds(body: Record<string, unknown>): Observable<SnapshotResponse<PreofferSettingsOverview>> {
    return this.wrap(this.http.put(`${environment.apiUrl}lease-preoffer-settings`, body), 'No se pudo guardar la configuración.')
  }

  changeMode(body: { mode: string; changeReason: string; acknowledged?: boolean }): Observable<SnapshotResponse<PreofferSettingsOverview>> {
    return this.wrap<PreofferSettingsOverview>(this.http.put(`${environment.apiUrl}lease-preoffer-settings/mode`, body), 'No se pudo cambiar el modo.').pipe(
      tap((response) => {
        if (!response.error) this.mode$ = null
      }),
    )
  }

  private shared(path: string): Observable<SnapshotResponse<ClientPreoffers>> {
    if (!this.cache.has(path)) {
      const request = this.wrap<ClientPreoffers>(this.http.get(`${environment.apiUrl}${path}`), 'No pudimos cargar la preoferta.').pipe(shareReplay({ bufferSize: 1, refCount: false }))
      this.cache.set(path, request)
    }
    return this.cache.get(path)!
  }

  private wrap<T>(source: Observable<unknown>, fallback: string): Observable<SnapshotResponse<T>> {
    return source.pipe(
      map((r: any) => ({ error: !!r?.error, msg: r?.msg ?? '', data: (r?.data ?? null) as T | null, status: 200 })),
      catchError((e: HttpErrorResponse) => of(this.toError<T>(e, fallback, e?.error))),
    )
  }

  private toError<T>(e: HttpErrorResponse, fallback: string, body: any): SnapshotResponse<T> {
    const status = e?.status ?? 0
    if (status === 0) return { error: true, msg: 'No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.', data: null, status, code: 'NETWORK' }
    const errors = Array.isArray(body?.data?.errors) ? body.data.errors.filter((item: unknown) => typeof item === 'string') : []
    const msg = errors.length ? errors.join('. ') : typeof body?.msg === 'string' && !/^bad request/i.test(body.msg) ? body.msg : fallback
    return { error: true, msg, data: null, status, code: body?.code ?? (status === 403 ? 'FORBIDDEN' : undefined) }
  }
}
